import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import Person2OutlinedIcon from "@mui/icons-material/Person2Outlined";
import { Box, Divider, Menu, MenuItem } from "@mui/material";
import { type FC, type MouseEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import type { Nurse, NurseInShift } from "../../utils/models";

type InvalidActualNursesMenuProps = {
	nurseId: Nurse["id"];
	shiftId: NurseInShift["id"];
	actionsMenuAnchorEl?: HTMLElement;
	onClose: () => void;
	/** When provided, shows an "Add to schedule" item (only for punched-but-unscheduled rows). */
	onAddToSchedule?: () => void;
	/**
	 * When provided, shows the destructive "Remove from shift" item. The caller decides whether the row
	 * may be removed at all (see `resolveActualRowRemoval`) and simply omits this prop when it may not —
	 * hiding rather than disabling, since a disabled item with no explanation is its own paper cut.
	 */
	onRemove?: () => void;
};

export const InvalidActualNursesMenu: FC<InvalidActualNursesMenuProps> = ({ nurseId, shiftId, actionsMenuAnchorEl, onClose, onAddToSchedule, onRemove }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { userInfo } = useAuth();
	const { showSnackbar } = useSnackbar();
	const { copyToClipboard } = useCopyToClipboard();
	const [copyMenuAnchorEl, setCopyMenuAnchorEl] = useState<HTMLElement | null>(null);

	const copyShiftId = useCallback(async (): Promise<void> => {
		const success = await copyToClipboard(shiftId);

		if (success) showSnackbar("Shift ID copied to clipboard", "success");
		else showErrorSnackbar("Failed to copy to clipboard", {});

		setCopyMenuAnchorEl(null);
		onClose();
	}, [shiftId, copyToClipboard, showSnackbar, showErrorSnackbar, onClose]);

	const copyNurseId = useCallback(async (): Promise<void> => {
		const success = await copyToClipboard(nurseId.toString());

		if (success) showSnackbar("Nurse ID copied to clipboard", "success");
		else showErrorSnackbar("Failed to copy to clipboard", {});

		setCopyMenuAnchorEl(null);
		onClose();
	}, [nurseId, copyToClipboard, showSnackbar, showErrorSnackbar, onClose]);

	const handleCopyMenuOpen = (event: MouseEvent<HTMLElement>): void => {
		event.stopPropagation();
		setCopyMenuAnchorEl(event.currentTarget);
	};

	const handleCopyMenuClose = (): void => {
		setCopyMenuAnchorEl(null);
	};

	const handleOuterClose = (): void => {
		if (copyMenuAnchorEl) return;
		onClose();
	};

	return (
		<>
			<Menu
				anchorEl={actionsMenuAnchorEl}
				open={!!actionsMenuAnchorEl}
				onClose={handleOuterClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				slotProps={{ paper: { sx: { mt: 1 } } }}
			>
				{onAddToSchedule && (
					<MenuItem
						data-testid="actual-menu-add-to-schedule"
						onClick={() => {
							onAddToSchedule();
							onClose();
						}}
					>
						<AddIcon fontSize={"small"} sx={{ marginRight: "8px" }} /> {t("actual.addToSchedule.menuItem")}
					</MenuItem>
				)}
				<MenuItem onClick={(_) => navigate(`/${chosenDepartment.department_id}/nurse/${nurseId}`, { state: { navigationSrc: window.location.pathname } })}>
					<Person2OutlinedIcon fontSize={"small"} sx={{ marginRight: "8px" }} /> Profile
				</MenuItem>
				{userInfo.support && (
					<MenuItem onClick={handleCopyMenuOpen}>
						<Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
							<ContentCopyOutlinedIcon fontSize={"small"} sx={{ marginRight: "8px" }} /> Copy
							<ChevronRightIcon fontSize={"small"} sx={{ marginLeft: "auto" }} />
						</Box>
					</MenuItem>
				)}
				{/* Destructive action last, behind its own divider — same grouping as `NurseMenu`'s Remove. */}
				{onRemove && <Divider sx={{ my: 1 }} />}
				{onRemove && (
					<MenuItem
						data-testid="actual-menu-remove"
						onClick={() => {
							onRemove();
							onClose();
						}}
					>
						<DeleteIcon fontSize={"small"} sx={{ marginRight: "8px" }} /> {t("actual.remove.menuItem")}
					</MenuItem>
				)}
			</Menu>
			<Menu
				anchorEl={copyMenuAnchorEl}
				open={!!copyMenuAnchorEl}
				onClose={handleCopyMenuClose}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				<MenuItem onClick={copyShiftId}>
					<ContentCopyOutlinedIcon fontSize={"small"} sx={{ marginRight: "8px" }} /> Copy Shift ID
				</MenuItem>
				<MenuItem onClick={copyNurseId}>
					<ContentCopyOutlinedIcon fontSize={"small"} sx={{ marginRight: "8px" }} /> Copy Nurse ID
				</MenuItem>
			</Menu>
		</>
	);
};
